<template>
    <div v-if="isMobileVersion">
        <section class="bg-white py-3 px-2.5 mb-[10px] flex items-center justify-between overflow-x-scroll">
            <router-link :to="{name: 'dashboard'}"><IconLogo/></router-link>
            <IconMenu @click="openMenu" class="w-8 h-8"/>
        </section>
        <div v-if="menuIsOpened" class="fixed top-0 left-0 bottom-0 right-0 bg-white z-20 py-3 px-2.5 flex flex-col items-start gap-6" style="padding-top: calc(env(safe-area-inset-top) + 0.75rem)">
            <div class="flex items-center justify-between w-full">
                <router-link :to="{name: 'dashboard'}"><IconLogo/></router-link>
                <IconClose @click="closeMenu" class="w-8 h-8"/>
            </div>
            <router-link :to="{name: 'account'}" @click="closeMenu" class="flex items-center gap-4 w-full">
                <IconPersonalData class="text-gray-500 w-16 h-16"/>
                <div class="flex flex-col items-start text-black" :class="activePage === 'account' ? '' : 'opacity-40'">
                    <p class="text-xl">{{ personalData?.name }}</p>
                    <p>{{ personalData?.email }}</p>
                </div>
            </router-link>
            <div class="flex flex-col items-start w-full gap-4 text-lg mb-auto">
                <router-link v-if="showCashRegisterButton" :to="{name: 'cash-register'}" @click="closeMenu" :class="activePage === 'cash-register' ? '' : 'opacity-40'" class="text-black">{{ $t('Current cash register') }}</router-link>
                <router-link v-if="showStatisticsButton" :to="{name: 'sales-search'}" @click="closeMenu" :class="activePage === 'sales-search' ? '' : 'opacity-40'" class="text-black">{{ $t("Sales search") }}</router-link>
                <router-link v-if="showAppointmentsButton" :to="{name: 'appointments'}" @click="closeMenu" :class="activePage === 'appointments' ? '' : 'opacity-40'" class="text-black">{{ $t("Appointments") }}</router-link>
                <router-link v-if="showClientsButton" :to="{name: 'clients'}" @click="closeMenu" :class="activePage === 'clients' ? '' : 'opacity-40'" class="text-black">{{ $t("Clients") }}</router-link>
                <router-link v-if="showCatalogButton" :to="{name: 'catalog'}" @click="closeMenu" :class="activePage === 'catalog' ? '' : 'opacity-40'" class="text-black">{{ $t("Catalog and supplies") }}</router-link>
<!--                <router-link v-if="showScheduleAndSalaryButton" :to="{name: 'schedule-and-salary'}" :class="activePage === 'staffs' ? '' : 'opacity-40'" class="text-black">{{ $t("Schedules and salaries") }}</router-link>-->
                <router-link v-if="showStaffsButton" :to="{name: 'staffs'}" @click="closeMenu" :class="activePage === 'staffs' ? '' : 'opacity-40'" class="text-black">{{ $t("Staff members") }}</router-link>
                <router-link v-if="!showStaffsButton && showScheduleAndSalaryButton" :to="{name: 'staffs'}" @click="closeMenu" :class="activePage === 'staffs' ? '' : 'opacity-40'" class="text-black">{{ $t("Schedules and salaries") }}</router-link>
                <router-link v-if="showPricingButton && !isPWA" :to="{name: 'pricing'}" @click="closeMenu" :class="activePage === 'pricing' ? '' : 'opacity-40'" class="text-black">{{ $t("Plans") }}</router-link>
                <router-link v-if="showStatisticsButton" :to="{name: 'statistics'}" @click="closeMenu" :class="activePage === 'statistics' ? '' : 'opacity-40'" class="text-black">{{ $t("Statistics") }}</router-link>
                <router-link v-if="showAIConciergeButton" :to="{name: 'ai-concierge'}" @click="closeMenu" :class="activePage === 'ai-concierge' ? '' : 'opacity-40'" class="text-black flex items-center gap-2">
                    {{ $t("AI concierge") }}
                    <span v-if="unansweredCount > 0" class="min-w-5 h-5 px-1.5 inline-flex items-center justify-center text-[0.625rem] font-semibold text-white bg-red-500 rounded-full">{{ unansweredCount }}</span>
                </router-link>
            </div>
            <div class="flex flex-col items-center gap-2.5 w-full">
                <router-link :to="{name: 'role'}" @click="closeMenu" class="w-full border border-gray-500 hover:bg-gray-500 text-center font-medium rounded text-sm px-5 py-2.5">{{ $t('Change role') }}</router-link>
                <button @click="signOut" class="w-full bg-custom-blue hover:bg-custom-blue-hover text-center text-white font-medium rounded text-sm px-5 py-2.5 mb-5">{{ $t("Sign out") }}</button>
            </div>
        </div>
    </div>
    <div v-else>
        <!-- Top bar -->
        <header class="fixed top-0 left-0 right-0 h-14 bg-gray-100 flex items-center justify-between px-6 z-20 shadow-[0_2px_8px_rgba(0,0,0,0.1),0_-2px_6px_rgba(0,0,0,0.04)]">
            <div class="flex items-center gap-4">
                <button @click="toggleSidebar" class="text-gray-500 hover:text-gray-700 transition-colors">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
                </button>
                <router-link :to="{name: 'role'}"><IconLogo/></router-link>
            </div>
            <div class="flex items-center gap-5 text-sm">
                <div v-if="amountDayToEndTrialPeriod >= 0 && role === CONSTANTS.ROLES.FOUNDER" class="flex items-center gap-2 text-xs text-gray-500">
                    <span>{{ amountDayToEndTrialPeriod === 0 ? $t("Your trial period ends today") : $t("Your trial period ends in {days} days", {days: amountDayToEndTrialPeriod}) }}</span>
                    <router-link v-if="$route.name !== 'pricing'" :to="{name: 'pricing'}" class="text-blue-600">{{ $t("Choose a plan →") }}</router-link>
                </div>
                <router-link :to="{ name: 'account' }" class="flex items-center gap-2 text-gray-700 hover:text-gray-900" :class="{'text-custom-blue': activePage === 'account'}">
                    <IconPersonalData class="w-5 h-5 shrink-0"/>
                    <span class="hidden lg:inline">{{ personalData?.name }}</span>
                </router-link>
                <div class="w-px h-5 bg-gray-300"></div>
                <router-link :to="{name: 'role'}" class="text-gray-500 hover:text-gray-700 text-xs">{{ $t("Change role") }}</router-link>
                <button @click="signOut" type="button" class="text-gray-400 hover:text-gray-600 transition-colors" :title="$t('Sign out')">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
                </button>
            </div>
        </header>
        <!-- Sidebar (below topbar) -->
        <aside class="fixed top-[3.75rem] left-0 bottom-0 w-52 bg-white border-r border-gray-200 flex flex-col z-10 transition-transform duration-200"
            :class="sidebarCollapsed ? '-translate-x-full' : 'translate-x-0'">
            <nav v-if="!onlyPersonalAccount && companyData?.['token']" class="flex-1 flex flex-col gap-1 px-2 py-3 overflow-y-auto">
                <router-link v-if="showCashRegisterButton && activePage === 'cash-register'" :to="{name: 'dashboard'}"
                    class="sidebar-link sidebar-active">
                    <svg class="sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                    {{ $t('Cash registers') }}
                </router-link>
                <router-link v-else-if="showCashRegisterButton" :to="{name: cashRegisterUrlName}"
                    class="sidebar-link sidebar-inactive">
                    <svg class="sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                    {{ cashRegisterUrlName === "dashboard" ? $t('Cash registers') : $t('Cash register') }}
                </router-link>
                <router-link v-if="showStatisticsButton" :to="{name: 'sales-search'}"
                    class="sidebar-link" :class="activePage === 'sales-search' ? 'sidebar-active' : 'sidebar-inactive'">
                    <svg class="sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                    {{ $t("Sales search") }}
                </router-link>
                <router-link v-if="showAppointmentsButton" :to="{name: 'appointments'}"
                    class="sidebar-link" :class="activePage === 'appointments' ? 'sidebar-active' : 'sidebar-inactive'">
                    <svg class="sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                    {{ $t("Appointments") }}
                </router-link>
                <router-link v-if="showClientsButton" :to="{name: 'clients'}"
                    class="sidebar-link" :class="activePage === 'clients' ? 'sidebar-active' : 'sidebar-inactive'">
                    <svg class="sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                    {{ $t("Clients") }}
                </router-link>
                <router-link v-if="showCatalogButton" :to="{name: 'catalog'}"
                    class="sidebar-link" :class="activePage === 'catalog' ? 'sidebar-active' : 'sidebar-inactive'">
                    <svg class="sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
                    {{ $t("Catalog and supplies") }}
                </router-link>
                <router-link v-if="showScheduleAndSalaryButton" :to="{name: 'schedule-and-salary'}"
                    class="sidebar-link" :class="activePage === 'staffs' ? 'sidebar-active' : 'sidebar-inactive'">
                    <svg class="sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                    {{ $t("Schedules and salaries") }}
                </router-link>
                <router-link v-if="showStaffsButton" :to="{name: 'staffs'}"
                    class="sidebar-link" :class="activePage === 'staffs' ? 'sidebar-active' : 'sidebar-inactive'">
                    <svg class="sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/></svg>
                    {{ $t("Staff members") }}
                </router-link>
                <router-link v-if="showStatisticsButton" :to="{name: 'statistics'}"
                    class="sidebar-link" :class="activePage === 'statistics' ? 'sidebar-active' : 'sidebar-inactive'">
                    <svg class="sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>
                    {{ $t("Statistics") }}
                </router-link>
                <router-link v-if="showAIConciergeButton" :to="{name: 'ai-concierge'}"
                    class="sidebar-link relative" :class="activePage === 'ai-concierge' ? 'sidebar-active' : 'sidebar-inactive'">
                    <svg class="sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/><circle cx="9" cy="9" r="1" fill="currentColor"/><circle cx="15" cy="9" r="1" fill="currentColor"/></svg>
                    {{ $t("AI concierge") }}
                    <span v-if="unansweredCount > 0" class="ml-auto min-w-5 h-5 px-1.5 inline-flex items-center justify-center text-[0.625rem] font-semibold text-white bg-red-500 rounded-full">{{ unansweredCount }}</span>
                </router-link>
                <router-link v-if="showPricingButton && !isPWA" :to="{name: 'pricing'}"
                    class="sidebar-link" :class="activePage === 'pricing' ? 'sidebar-active' : 'sidebar-inactive'">
                    <svg class="sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z"/></svg>
                    {{ $t("Plans") }}
                </router-link>
            </nav>
        </aside>
    </div>
</template>


<script>
    import {mapState} from 'vuex';
    import {ROLES} from "@/constants";

    import IconLogo from './Icon/IconLogo.vue';
    import IconMenu from "@/components/Icon/IconMenu.vue";
    import IconPersonalData from "@/components/Icon/IconPersonalData.vue";
    import IconClose from "@/components/Icon/IconClose.vue";

    export default {
        name: "MainHeaderComponent",
        components: {
            IconClose,
            IconLogo,
            IconMenu,
            IconPersonalData,
        },
        props: {
            activePage: {
                type: String,
                required: false,
            },
            onlyPersonalAccount: {
                type: Boolean,
                default: false,
            }
        },
        data() {
            return {
                CONSTANTS: {
                    ROLES: ROLES,
                },
                menuIsOpened: false,
                sidebarCollapsed: localStorage.getItem("sidebarCollapsed") === "true",
            };
        },
        computed: {
            ...mapState("authentication", ["isMobileVersion", "isPWA"]),
            ...mapState("personalData", ["role", "personalData", "personalStaffData"]),
            ...mapState("company", ["companyData"]),
            ...mapState("cashRegister", ["cashRegisterData"]),
            cashRegisterUrlName() {
                const currentCashRegisterId = this.$globalMethods.getDataFromLocalStorage("currentCashRegisterId");
                if (currentCashRegisterId) return "cash-register";
                return "dashboard";
            },
            showCashRegisterButton() {
                if (this.isMobileVersion) {
                    return this.$globalMethods.getDataFromLocalStorage("currentCashRegisterId");
                }
                return true
            },
            showClientsButton() {
                return true;
            },
            showCatalogButton() {
                return true;
            },
            showStaffsButton() {
                return this.role === this.CONSTANTS.ROLES.FOUNDER;
            },
            showScheduleAndSalaryButton() {
                return this.role === this.CONSTANTS.ROLES.STAFF && this.companyData?.["plan"]?.["schedule_and_salary_plugin_is_active"];
            },
            showAppointmentsButton() {
                if (!this.companyData?.plan?.appointments_plugin_is_active) return false;
                if (this.role === this.CONSTANTS.ROLES.FOUNDER) return true;
                if (this.role === this.CONSTANTS.ROLES.STAFF) {
                    return this.personalStaffData?.view_appointments_permission || this.personalStaffData?.manage_appointments_permission;
                }
                return false;
            },
            showAIConciergeButton() {
                if (!this.companyData?.ai_concierge_available) return false;
                if (this.role === this.CONSTANTS.ROLES.FOUNDER) return true;
                if (this.role === this.CONSTANTS.ROLES.STAFF) {
                    return !!this.personalStaffData?.manage_ai_concierge_permission;
                }
                return false;
            },
            unansweredCount() {
                return this.$store.getters["aiConcierge/unansweredCount"] || 0;
            },
            showStatisticsButton() {
                return this.role === this.CONSTANTS.ROLES.FOUNDER
                    || (this.role === this.CONSTANTS.ROLES.STAFF && this.personalStaffData?.view_statistics_permission);
            },
            showPricingButton() {
                return this.role === this.CONSTANTS.ROLES.FOUNDER;
            },
            amountDayToEndTrialPeriod() {
                if (!this.companyData?.["plan"]) return -1;

                const todayString = new Date().toString();
                const dateOfEndTrialPeriodString = this.companyData["plan"]["date_of_end_trial_period"];
                return this.$globalMethods.getAmountOfDaysBetweenDates(todayString, dateOfEndTrialPeriodString);
            },
        },
        methods: {
            openMenu() {
                this.menuIsOpened = true;
                document.body.classList.add("overflow-hidden");
            },
            closeMenu() {
                this.menuIsOpened = false;
                document.body.classList.remove("overflow-hidden");
            },
            toggleSidebar() {
                this.sidebarCollapsed = !this.sidebarCollapsed;
                localStorage.setItem("sidebarCollapsed", this.sidebarCollapsed);
                window.dispatchEvent(new CustomEvent("sidebar-toggled", {detail: {collapsed: this.sidebarCollapsed}}));
            },
            async signOut() {
                this.$router.push({name: "sign-out"});
            },
        },
    }
</script>

<style scoped>
.sidebar-link {
    @apply flex items-center gap-2.5 px-3 py-2.5 rounded-md transition-colors text-nowrap truncate text-[0.875rem];
}
.sidebar-icon {
    @apply w-[1.15rem] h-[1.15rem] shrink-0;
}
.sidebar-active {
    @apply bg-blue-50 text-custom-blue font-medium;
}
.sidebar-inactive {
    @apply text-gray-600 hover:bg-gray-100 hover:text-gray-900;
}
</style>